import {Directive, ElementRef, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import Inputmask from 'inputmask';
import {AppService} from '../../../app/app.service';
import {AbstractControl, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';

@Directive({
    selector: '[app-restrict-input]',
    host: {'(paste)': '_handlePaste(null)'},
    standalone: true
})
export class RestrictInputDirective implements OnInit, OnDestroy {
    @Input('app-restrict-input-control') control: AbstractControl;
    //карта некоторых строк регулярных выражений, которые используются
    private regexMap = {
        integer: '^[-]{0,1}\\d+$',
        color: '^\#+([a-fA-F0-9]{6})$',
        date: '[0-3][0-9]\\.[0-1][0-9]\\.[0-9]{4}',
        float: '^[+-]?([0-9]*[.])?[0-9]+$',
        double: '^-?\\d+(\\.\\d+)?$',
        words: '([A-z]*\\s)*',
        point25: '^\-?[0-9]*(?:\\.25|\\.50|\\.75|)$',
        phoneEmail: '(\\+[0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])|([0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])|([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4})',
        email: '([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4})',
        phone: '(\\+[0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])|([0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])',
    };

    /**
     * Карта дефолтных масок
     * @private
     */
    private _mapMask = {};
    /**
     * Тип маски
     */
    private type = null;
    /**
     * Исключения для события вставки в поле данных
     */
    private excludeTypes = {
        date: true,
        dateTime: true
    };

    public valueChanges$: Subscription;

    /**
     * Конструктор
     * @param el ссылка на элемент
     * @param appService сервис
     */
    constructor(
        private el: ElementRef,
        private appService: AppService
    ) {
    }

    /**
     * Проставление значения в поле после вставки
     * @param $event событие
     * @private
     */
    @HostListener('paste', ['$event'])
    _handlePaste($event: ClipboardEvent) {
        if (this.control && !this.excludeTypes[this.type]) {
            setTimeout(() => {
                //получаем текуще значение маски
                if (this.el?.nativeElement?.inputmask) {
                    let currentMaskValue = null;
                    if (this.el?.nativeElement?.inputmask._valueGet()) {
                        currentMaskValue = this.el?.nativeElement?.inputmask?._valueGet();
                    }
                    //при отличии результатов ставим значение маски
                    if (currentMaskValue && currentMaskValue != this.control.value) {
                        this.control.setValue(currentMaskValue);
                    }
                }
            });
        }
    }

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        if (this.control) {
            this.valueChanges$ = this.control.valueChanges.subscribe((value) => {
                if (value && this.el.nativeElement && this.el.nativeElement.inputmask) {
                    const isFloatRegex = this.el.nativeElement.inputmask.maskset.mask === '[-]9{1,}[.|,]{0,1}9{1,}';
                    const isValueInteger = Number.isInteger(+this.el.nativeElement.inputmask.maskset.buffer.join(''));
                    if (!this.el.nativeElement.inputmask.isValid()) {
                        // не показывать ошибку, если в поле с float указан integer т.к.
                        // все равно добавится дробная часть
                        if (isValueInteger && isFloatRegex) {
                            return;
                        }
                        this.control.setErrors({
                            inputMaskError: this.appService.language_dictionary.system_input_mask_error
                        });
                        this.control.markAsTouched({onlySelf: true});
                    }
                }
            });
        }
    }

    ngOnDestroy() {
        if (this.valueChanges$) {
            this.valueChanges$.unsubscribe();
        }
    }

    /**
     * Определение типа маски
     * @param type тип
     */
    @Input('app-restrict-input')
    public set defineInputType(type: string) {
        if (typeof type !== 'string' || type.length < 1) {
            return;
        }

        this.type = type;
        Inputmask.remove(this.el.nativeElement);
        if (type == 'date') {
            const dateSeparator = this.appService.language_dictionary.system_date_format_separator;
            Inputmask({
                alias: 'datetime',
                inputFormat: this.appService.language_dictionary.system_date_format_input_mask,
                placeholder: '_',
                prefillYear: false,
                preValidation: (buffer, pos, char) => {
                    if ((pos === 2 || pos === 5) && char !== dateSeparator) {
                        return {
                            insert: [
                                {pos, c: dateSeparator},
                                {pos: pos + 1, c: char}],
                            caret: {
                                begin: pos + 1,
                                end: pos + 1
                            }
                        };
                    }
                    return true;
                }
            }).mask(this.el.nativeElement);
        } else if (type == 'dateTime') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'dd.mm.yyyy HH:MM',
                placeholder: '_'
            }).mask(this.el.nativeElement);
        } else if (type == 'dayMonth') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'dd.mm',
                placeholder: '_',
                autoUnmask: true
            }).mask(this.el.nativeElement);
        } else if (type == 'dateMonth') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'mm.yyyy',
                placeholder: '_'
            }).mask(this.el.nativeElement);
        } else if (type == 'dateYear') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'yyyy',
                placeholder: '_'
            }).mask(this.el.nativeElement);
        } else if (type == 'email') {
            Inputmask({alias: 'email'}).mask(this.el.nativeElement);
        } else if (type == 'float') {
            Inputmask({
                mask: '[-]9{1,}[.|,]{0,1}9{1,}',
                placeholder: '',
                removeMaskOnSubmit: true,
                substitutes: {
                    ",": "."
                },
                onBeforeMask: (val) => {
                    if (Number.isInteger(val)) {
                        this.control.setValue(val + '.0');
                        return val + '.0';
                    }
                    return val;
                },
                onBeforePaste: (val) => {
                    val = parseFloat((String(val)).replace(',', '.'));
                    if (Number.isInteger(val)) {
                        return val + '.0';
                    }
                    return val;
                },
                onincomplete: (val) => {
                    let value = this.control.value;
                    const splitval = (value.replace(',', '.').split('.'));
                    if (splitval.length > 0 && splitval.length === 1 || splitval[1] === '') {
                        value = value.replace('.', '');
                        value = String(value) + '.0';
                    }
                    this.control.setValue(value, {eventEmit: false});
                    val.target.inputmask._valueSet(value);
                    this.control.updateValueAndValidity({emitEvent: false});
                    return val;
                }
            }).mask(this.el.nativeElement);
        } else if (type == 'integer') {
            Inputmask({
                regex: '^[-]{0,1}\\d+$',
                placeholder: '',
            }).mask(this.el.nativeElement);
        } else if (type == 'page-number') {
            Inputmask({
                regex: '^[1-9][0-9]+$',
                placeholder: ''
            }).mask(this.el.nativeElement);
        } else if (type == 'color') {
            Inputmask({
                regex: '^\#+([a-fA-F0-9]{6})$',
                placeholder: '',
            }).mask(this.el.nativeElement);
        } else if (this._mapMask[type]) {
            Inputmask({mask: this._mapMask[type]}).mask(this.el.nativeElement);
        } else if (type == 'double') {
            Inputmask({
                mask: '[-]9{1,}[.|,|]{0,1}9{1,}',
                placeholder: '',
            }).mask(this.el.nativeElement);
            setTimeout(() => {
                this.control.addValidators([Validators.pattern(new RegExp(this.regexMap[type]))]);
                this.control.updateValueAndValidity({onlySelf: true, emitEvent: false});
            }, 1);
        } else if (this.regexMap[type]) {
            Inputmask({regex: this.regexMap[type]}).mask(this.el.nativeElement);
        } else if (type && typeof type == 'string') {
            Inputmask({
                mask: type,
                placeholder: '',
            }).mask(this.el.nativeElement);
        }
    }

    /**
     * Определение маски по регулярному выражению
     * @param type тип
     */
    @Input('app-restrict-input-regex')
    public set defineInputRegexType(type: string) {
        if (type?.length > 0) {
            setTimeout(() => {
                const regex = new RegExp(type, 'i');
                this.control.addValidators(Validators.pattern(regex));
                this.control.updateValueAndValidity();
            });
        }
    }
}
